import React from 'react';
import { useSubscription } from '../contexts/SubscriptionProvider';
import { Lock } from 'lucide-react';

/**
 * Conditionally render children based on plan feature access.
 *
 * @param {string} feature - Feature key from FEATURES constant
 * @param {React.ReactNode} children - Content to render if feature is enabled
 * @param {React.ReactNode} fallback - Custom fallback UI (optional)
 * @param {boolean} hide - If true, render nothing when feature is unavailable (no fallback)
 *
 * Usage:
 *   <FeatureGate feature={FEATURES.CONTRACT_MODULE}>
 *     <ContractScreen />
 *   </FeatureGate>
 */
export const FeatureGate = ({ feature, children, fallback = null, hide = false }) => {
    const { isFeatureEnabled } = useSubscription();

    if (isFeatureEnabled(feature)) {
        return children;
    }

    if (hide) return null;

    return fallback || (
        <div className="flex items-center gap-2 text-muted-foreground opacity-60 cursor-not-allowed select-none">
            <Lock size={14} />
            <span className="text-xs">Upgrade to Pro</span>
        </div>
    );
};

/**
 * Conditionally render children based on rate limit status.
 *
 * @param {string} feature - Feature key from FEATURES constant
 * @param {React.ReactNode} children - Content to render if under limit
 * @param {React.ReactNode} fallback - Custom fallback UI (optional)
 *
 * Usage:
 *   <RateLimitGate feature={FEATURES.AI_CHAT}>
 *     <ChatInput />
 *   </RateLimitGate>
 */
export const RateLimitGate = ({ feature, children, fallback = null }) => {
    const { isAtLimit, getUsage } = useSubscription();

    if (!isAtLimit(feature)) {
        return children;
    }

    const usage = getUsage(feature);

    return fallback || (
        <div className="text-center p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium">
                Monthly limit reached ({usage?.used}/{usage?.limit})
            </p>
            <p className="text-xs text-amber-600 mt-1">
                Upgrade to Pro for unlimited access.
            </p>
        </div>
    );
};
